/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import {
  IlCard, IlIconInitializer
} from 'il-framework-component';
import { useTranslation } from 'react-i18next';
import Wrapper from '../../app/shared/components/Wrapper/Wrapper';
import BackButton from '../../app/shared/components/BackButton/BackButton';
import VerifyAuthenticator from './VerifyMethods/Authenticator/VerifyAuthenticator';
import VerifySMS from './VerifyMethods/SMS/VerifySMS';
import VerifyEmail from './VerifyMethods/Email/VerifyEmail';
import styles from './SecuritySetup.scss';
import { I18nNamespaces } from '../../app/shared/utils/constants.util';
import usePreserveSearchNav from '../../app/shared/hooks/usePreserveSearchNav';
import { setSelectedFactor } from './setupSecuritySlice';
import { SecuritySetupRoutes } from '../../app/shared/utils/securitySetupRoutes';

type VerifyMethodKey = 'authenticator' | 'sms' | 'push' | 'email';

const VIEWS: Partial<Record<VerifyMethodKey, React.ComponentType>> = {
  authenticator: VerifyAuthenticator,
  sms: VerifySMS,
  email: VerifyEmail
};

const VerifyMethod: React.FC = () => {
  const { method } = useParams<{ method: VerifyMethodKey }>();
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const { replacePreserve, goBackOrReplace } = usePreserveSearchNav();

  useEffect(() => {
    if (!VIEWS[method]) replacePreserve(SecuritySetupRoutes.root);
  }, [method, replacePreserve]);

  useEffect(() => () => {
    dispatch(setSelectedFactor(undefined));
  }, [dispatch]);

  const MethodView = VIEWS[method];
  if (!MethodView) return null;

  const handleBackClick = (): void => {
    goBackOrReplace(SecuritySetupRoutes.root);
  };

  return (
    <>
      <IlIconInitializer />
      <Wrapper>
        <IlCard className={styles.cardForm}>
          <BackButton
            title={t('loaEnrichment.common.buttons.backToPreviousPage')}
            onClick={handleBackClick}
            className={styles.backBtn}
          />
          {/* This is the only place that switches views */}
          <MethodView />
        </IlCard>
      </Wrapper>
    </>
  );
};

export default VerifyMethod;
